import type { TextParsedTrack } from '../model/track.ts';
import { parseClockTime, parseTickTime } from './clock.ts';

const TTML_TICK_RATE = 10_000_000;

type TtmlBeginElement = {
  begin: string;
  end: string | undefined;
  innerHtml: string;
};

function parseAttributeMap(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const match of attrString.matchAll(/\b([\w:]+)="([^"]*)"/g)) {
    if (match[1]) attrs[match[1]] = match[2] ?? '';
  }
  return attrs;
}

/**
 * Find elements with a `begin` attribute — pure equivalent of
 * querySelectorAll('[begin]') used by the ref / shipping DOM path.
 * Matches any tag name (not only `<p>`).
 */
function findBeginElements(xml: string): TtmlBeginElement[] {
  const elements: TtmlBeginElement[] = [];
  const regex = /<([A-Za-z][\w:.-]*)\b([^>]*\bbegin\s*=\s*"[^"]*"[^>]*)>([\s\S]*?)<\/\1\s*>/gi;
  for (const match of xml.matchAll(regex)) {
    const attrs = parseAttributeMap(match[2] ?? '');
    if (!attrs.begin) continue;
    elements.push({
      begin: attrs.begin,
      end: attrs.end,
      innerHtml: match[3] ?? '',
    });
  }
  return elements;
}

/** Netflix TTML: tick times + raw innerHTML. Mirrors functions/netflix.js parseTextBasedTrack. */
export function parseNetflixTextTtml(xml: string): TextParsedTrack {
  const tracks: TextParsedTrack['tracks'] = [];
  for (const el of findBeginElements(xml)) {
    tracks.push({
      begin: parseTickTime(el.begin, TTML_TICK_RATE),
      end: parseTickTime(el.end, TTML_TICK_RATE),
      html: el.innerHtml,
    });
  }
  return { type: 'text', tracks };
}

/** Prime TTML: clock times + br-normalized html. Mirrors functions/primevideo.js parseTextBasedTrack. */
export function parsePrimeTextTtml(xml: string): TextParsedTrack {
  const tracks: TextParsedTrack['tracks'] = [];
  for (const el of findBeginElements(xml)) {
    const html = el.innerHtml
      .replaceAll(/<br[^>]*>/gi, '\n')
      .replaceAll(/<[^>]*>/g, '')
      .replaceAll(/(?:\r\n|\r|\n)/g, '<br>');
    tracks.push({
      begin: parseClockTime(el.begin),
      end: parseClockTime(el.end),
      html,
    });
  }
  return { type: 'text', tracks };
}

export { TTML_TICK_RATE };
